import { en } from "./locales/en";
import { pl } from "./locales/pl";

export type Language = "pl" | "en";
type ReplaceLeafStrings<T> =
  T extends string
    ? string
    : T extends readonly (infer U)[]
      ? readonly ReplaceLeafStrings<U>[]
      : T extends object
        ? { readonly [K in keyof T]: ReplaceLeafStrings<T[K]> }
        : T;

export type Dictionary = ReplaceLeafStrings<typeof pl>;

const FALLBACK_LANGUAGE: Language = "pl";
const rawDictionaries = { pl, en } satisfies Record<Language, Dictionary>;
const POLISH_ORPHAN_WORDS = ["i", "w", "z", "a", "o", "u", "do", "na"];

function isLanguage(value: string): value is Language {
  return value === "pl" || value === "en";
}

export function fixTypography(text: string): string {
  const escapedWords = POLISH_ORPHAN_WORDS.join("|");
  const pattern = new RegExp(`\\b(${escapedWords})\\s+`, "gi");
  return text.replace(pattern, (_, word: string) => `${word}\u00A0`);
}

function applyTypographyRecursively<T>(value: T): T {
  if (typeof value === "string") {
    return fixTypography(value) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => applyTypographyRecursively(item)) as T;
  }
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).map(([key, nested]) => [
      key,
      applyTypographyRecursively(nested),
    ]);
    return Object.fromEntries(entries) as T;
  }
  return value;
}

const dictionaries: Record<Language, Dictionary> = {
  pl: applyTypographyRecursively(rawDictionaries.pl),
  en: rawDictionaries.en,
};

export function getDictionary(lang: string): Dictionary {
  return isLanguage(lang) ? dictionaries[lang] : dictionaries[FALLBACK_LANGUAGE];
}
